import React, {useEffect, useState} from "react";
import {AppSelect} from "../ui/AppSelect";
import {makeStyles} from "@mui/styles";
import {ApiRequestMethods} from "../../constants/AppConstants";
import {AppField} from "../ui/AppField";
import cx from "classnames";
import {AnimateSharedLayout, motion} from "framer-motion";
import {IntegrationApiParams} from "../integrations/auth/IntegrationApiParams";
import {AppButton} from "../ui/AppButton";
import {Controlled} from "react-codemirror2";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/dracula.css";
import "codemirror/theme/eclipse.css";
import "codemirror/mode/javascript/javascript";
import {IntegrationApiHeaders} from "../integrations/auth/IntegrationApiHeaders";
import {IntegrationApiBody} from "../integrations/auth/IntegrationApiBody";
import {ApiEndpointsProps} from "../../api/integrations/IntegrationsDTO";
import {Loader} from "../ui/Loader";
import {Box} from "@mui/material";

const useStyles = makeStyles({
    root: {},
    url: {
        gap: 20,
        marginBottom: 16,
    },
    tabWrapper: {
        border: "1px solid #d9d9d9",
        borderRadius: 20,
        padding: 16,
    },
    tabHeader: {
        display: "flex",
        gap: 40,
        borderBottom: "1px solid #E8E8E8",
    },
    tabHeaderItem: {
        padding: "10px 4px",
        cursor: "pointer",
        position: "relative",
        transition: "0.3s",
        "&.active": {
            color: "#3F51B5",
        },
    },
    tabBody: {
        padding: "16px 0",
        overflow: "hidden",
    },
    tabBodyItem: {
        display: "none",
        "&.active": {
            display: "block",
        },
    },
    activeLine: {
        position: "absolute",
        width: "100%",
        top: "100%",
        height: "2.5px",
        background: "#3F51B5",
        left: 0,
    },
    title: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 15,
    },
    editor: {
        borderRadius: 8,
        overflow: "hidden",
    },
    conLabel: {
        marginTop: 16,
    },
});

const Tabs = [
    {
        title: "Params",
        id: 1,
        component: IntegrationApiParams,
    },
    {
        title: "HTTP Headers",
        id: 2,
        component: IntegrationApiHeaders,
    },
    {
        title: "Request Body",
        id: 3,
        component: IntegrationApiBody,
    },
];

interface Props {
    readonly apiEndpoint: ApiEndpointsProps;
    readonly onChange: (value: any) => void;
    readonly handleSubmit: (data: any) => void;
    readonly authEditor?: boolean;
}

export function ApiEndPointEditor({
                                      apiEndpoint,
                                      handleSubmit,
                                      onChange,
                                      authEditor,
                                  }: Props) {
    const classes = useStyles();
    const [activeTab, setActiveTab] = useState(1);
    const [codeMode, setCodeMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [params, setParams] = useState<any[]>([]);
    const [body, setBody] = useState<any[]>([]);
    const [headers, setHeaders] = useState<any[]>([]);
    const [data, setData] = useState<ApiEndpointsProps>({
        headers: {},
        method: "GET",
        params: {},
        requestBody: {},
        source: "",
        switchedToSource: false,
        url: "",
    });

    const handleDataChange = (name: string, value: any) => {
        //@ts-ignore
        setData((prev) => {
            const newData = {
                ...prev,
                [name]: value,
            };
            onChange({...newData});
            return newData;
        });
    };

    useEffect(() => {
        if (apiEndpoint) {
            setCodeMode(apiEndpoint.switchedToSource);
            setData(apiEndpoint);
            setLoading(false);
            if (apiEndpoint.params) {
                setParams(() => {
                    let arr = Object.keys(apiEndpoint.params).map((item) => {
                        return {
                            id: Date.now() + Math.random() * 100,
                            code: item,
                            value: apiEndpoint.params[item],
                            saved: true,
                        };
                    });
                    return [...arr];
                });
            }

            if (apiEndpoint.requestBody) {
                setBody(() => {
                    let arr = Object.keys(apiEndpoint.requestBody).map((item) => {
                        return {
                            id: Date.now() + Math.random() * 100,
                            code: item,
                            value: apiEndpoint.requestBody[item],
                            saved: true,
                        };
                    });
                    return [...arr];
                });
            }

            if (apiEndpoint.headers) {
                setHeaders(() => {
                    let arr = Object.keys(apiEndpoint.headers).map((item) => {
                        return {
                            id: Date.now() + Math.random() * 100,
                            code: item,
                            value: apiEndpoint.headers[item],
                            saved: true,
                        };
                    });
                    return [...arr];
                });
            }
        } else {
            setLoading(false);
        }
    }, [apiEndpoint]);
    return loading ? (
        <Loader/>
    ) : (
        <div>
            <div className={classes.title}>
                <h2>API Endpoint</h2>
                <AppButton
                    onClick={() => {
                        setCodeMode((prev) => {
                            handleDataChange("switchedToSource", !prev);
                            return !prev;
                        });
                    }}
                >
                    {codeMode ? "Switch To Form Mode" : "Switch To Source Mode"}
                </AppButton>
            </div>
            {codeMode ? (
                <div className={classes.editor}>
                    {/*@ts-ignore*/}
                    <Controlled
                        onBeforeChange={(editor, data, value) => {
                            handleDataChange("source", value);
                        }}
                        value={data.source}
                        options={{
                            lineWrapping: true,
                            lang: "javascript",
                            mode: "javascript",
                            theme: "dracula",
                            lineNumbers: true,
                            gutters: ["CodeMirror-lint-markers"],
                        }}
                    />
                </div>
            ) : (
                <>
                    <Box sx={{display: "flex", alignItems: "center"}} className={classes.url}>
                        <AppSelect
                            onValueChange={(value) => handleDataChange("method", value)}
                            defaultValue={data.method}
                            options={Object.keys(ApiRequestMethods).map((item) => ({
                                label: item,
                                value: ApiRequestMethods[item],
                            }))}
                        />
                        <AppField
                            required
                            placeholder="ex. https://www.example.com"
                            fullWidth
                            name="url"
                            onChange={(e: any) => handleDataChange("url", e.target.value)}
                            value={data.url}
                        />
                    </Box>

                    <div className={classes.tabWrapper}>
                        <div className={classes.tabHeader}>
                            {Tabs.map((item) => {
                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => setActiveTab(item.id)}
                                        className={cx(classes.tabHeaderItem, {
                                            active: activeTab === item.id,
                                        })}
                                    >
                    <span onClick={() => setActiveTab(item.id)}>
                      {item.title}
                    </span>
                                        {activeTab === item.id && (
                                            <motion.div
                                                animate={{scaleX: 1}}
                                                initial={{scaleX: 0}}
                                                layoutId="line"
                                                className={classes.activeLine}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={classes.tabBody}>
                            <AnimateSharedLayout>
                                {Tabs.map((item) => {
                                    return (
                                        <div
                                            key={item.id}
                                            className={cx(classes.tabBodyItem, {
                                                active: activeTab === item.id,
                                            })}
                                        >
                                            <item.component
                                                data={{
                                                    params: params,
                                                    headers: headers,
                                                    body: body,
                                                }}
                                                onChange={(name: string, value: any) => {
                                                    handleDataChange(name, value);
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </AnimateSharedLayout>
                        </div>
                    </div>
                </>
            )}
            <div className="">
                <AppButton
                    sx={{mt: 1}}
                    onClick={() => handleSubmit(data)}
                >
                    Save
                </AppButton>
            </div>
        </div>
    );
}
